/**
 * ACA "Applicable Percentage" curves — the maximum % of MAGI a
 * household pays for the SLCSP (Second Lowest Cost Silver Plan)
 * before subsidy. Subsidy = SLCSP_premium - (MAGI × applicable_pct).
 *
 * **The political backdrop**:
 *
 * - **Pre-ARPA baseline** (original ACA, IRC § 36B(b)(3)(A)(i)):
 *   2.07% at 138% FPL → 9.86% at 300%+ FPL, with a HARD CLIFF at
 *   400% FPL (over 400% = $0 subsidy = full premium owed).
 *
 * - **ARPA + IRA extension** (American Rescue Plan Act 2021 §
 *   9661 + Inflation Reduction Act 2022 § 12001): subsidy curve was
 *   smoothed AND the cliff was eliminated, with a top end of 8.5%
 *   for 400%+ FPL households. Originally for 2021-2022; IRA
 *   extended through plan year 2025.
 *
 * - **Plan year 2026 outlook**: as of 2026-05-08, Congress has NOT
 *   yet passed an extension beyond 2025. Current law reverts to
 *   pre-ARPA baseline for 2026 unless Congress acts. Hearth ships
 *   BOTH curves and lets the user toggle the assumption — modeling
 *   both is critical for early-retiree planning since the cliff
 *   sensitivity is huge ($1,500-2,500/mo premium swing per family).
 *
 * **Sources** (all retrieved 2026-05-08):
 *   - IRC § 36B (original ACA premium tax credit)
 *   - ARPA § 9661 (March 2021)
 *   - IRA § 12001 (August 2022)
 *   - IRS Rev Proc 2024-35 (applicable percentage table for plan year 2025)
 *
 * **Self-contained types** — file mirrors verbatim from
 * `shared/src/data/` to `web/src/lib/data/`.
 */

export type AcaPercentageCurveKind = "ira_extended" | "pre_arpa_baseline";

/** A breakpoint in the applicable-percentage curve. Linear
 *  interpolation between consecutive breakpoints. */
export interface AcaPercentageBreakpoint {
  readonly fpl_pct: number;
  readonly applicable_pct: number;
}

export interface AcaPercentageCurve {
  readonly kind: AcaPercentageCurveKind;
  readonly description: string;
  readonly source: string;
  readonly breakpoints: readonly AcaPercentageBreakpoint[];
  /** If true, MAGI > the highest fpl_pct breakpoint = NO subsidy
   *  (cliff). If false, the curve flattens at the top breakpoint
   *  rate. */
  readonly cliff_at_top: boolean;
  /** Cliff threshold in FPL %, when `cliff_at_top` is true.
   *  Pre-ARPA: 400%. */
  readonly cliff_at_fpl_pct: number;
}

/** ARP/IRA-extended curve, in effect through plan year 2025. Hearth
 *  defaults to this for 2026 IF the user assumes Congress extends. */
export const ACA_CURVE_IRA_EXTENDED: AcaPercentageCurve = {
  kind: "ira_extended",
  description:
    "ARP+IRA-extended applicable percentage (in effect through plan year 2025; assumes extension to 2026)",
  source:
    "ARPA § 9661 + IRA § 12001 + IRS Rev Proc 2024-35 (plan year 2025)",
  breakpoints: [
    { fpl_pct: 0, applicable_pct: 0 },
    { fpl_pct: 150, applicable_pct: 0 },
    { fpl_pct: 200, applicable_pct: 2.0 },
    { fpl_pct: 250, applicable_pct: 4.0 },
    { fpl_pct: 300, applicable_pct: 6.0 },
    { fpl_pct: 400, applicable_pct: 8.5 },
    // Beyond 400% FPL: flatlines at 8.5% (no cliff under ARP/IRA).
    { fpl_pct: 1000, applicable_pct: 8.5 },
  ],
  cliff_at_top: false,
  cliff_at_fpl_pct: 400,
};

/** Pre-ARPA original ACA curve. Reverts in 2026 unless Congress
 *  extends. */
export const ACA_CURVE_PRE_ARPA: AcaPercentageCurve = {
  kind: "pre_arpa_baseline",
  description:
    "Original ACA applicable percentage (current law for 2026 plan year unless Congress extends ARP/IRA enhancements)",
  source: "IRC § 36B(b)(3)(A)(i) — original ACA",
  breakpoints: [
    { fpl_pct: 0, applicable_pct: 0 },
    { fpl_pct: 138, applicable_pct: 2.07 },
    { fpl_pct: 150, applicable_pct: 4.14 },
    { fpl_pct: 200, applicable_pct: 6.49 },
    { fpl_pct: 250, applicable_pct: 8.29 },
    { fpl_pct: 300, applicable_pct: 9.86 },
    { fpl_pct: 400, applicable_pct: 9.86 },
  ],
  // Hard cliff at 400% FPL — above this, NO premium tax credit.
  cliff_at_top: true,
  cliff_at_fpl_pct: 400,
};

/**
 * Linear-interpolate the applicable percentage at the household's
 * `fpl_pct` (% of FPL). Returns 0 if below first breakpoint or above
 * top with no cliff. When cliff_at_top + above cliff, returns -1
 * (caller's job to interpret as "no subsidy" / full premium).
 */
export function applicablePercentage(
  fpl_pct: number,
  curve: AcaPercentageCurve,
): number {
  if (fpl_pct < 0) return 0;
  if (curve.cliff_at_top && fpl_pct > curve.cliff_at_fpl_pct) {
    return -1;
  }
  const bps = curve.breakpoints;
  for (let i = 0; i < bps.length - 1; i++) {
    const a = bps[i]!;
    const b = bps[i + 1]!;
    if (fpl_pct >= a.fpl_pct && fpl_pct <= b.fpl_pct) {
      if (a.applicable_pct === b.applicable_pct) return a.applicable_pct;
      const t = (fpl_pct - a.fpl_pct) / (b.fpl_pct - a.fpl_pct);
      return a.applicable_pct + t * (b.applicable_pct - a.applicable_pct);
    }
  }
  // Beyond top breakpoint (only reachable if !cliff_at_top).
  return bps[bps.length - 1]?.applicable_pct ?? 0;
}
